import { useTranslation } from "react-i18next";
import { SCOPE_VALUE } from "./ScopeOptions";
import "./CommissionRuleSummary.css";

/**
 * A regra escrita em uma frase, atualizada enquanto o formulário é preenchido.
 *
 * Existe porque a regra é montada em quatro campos espalhados (para quem, em quê, quanto e
 * quando) e o usuário não tinha onde conferir o conjunto antes de salvar. Ler "João recebe 40%
 * em Corte a partir de 01/09" responde de uma vez se a regra é a que ele queria, sem precisar
 * entender os termos do cadastro.
 */
const CommissionRuleSummary = ({ values }) => {
    const { t, i18n } = useTranslation();

    // Lacuna visível no lugar do que falta preencher: a frase continua legível e mostra onde
    // o usuário ainda precisa agir.
    const missing = <span className="commission-rule-summary__missing">___</span>;
    const bold = (text) => <strong>{text}</strong>;
    const formatDay = (date) => (date ? new Date(date).toLocaleDateString(i18n.language) : null);

    const who = values.employee
        ? <>{bold(values.employee.label)} {t('commission_rule_summary_receives_one')}</>
        : t('commission_rule_summary_receives_all');

    const howMuch = () => {
        if (values.paymentType === 'TIERED') {
            const percents = (values.tiers || [])
                .map(tier => Number(tier.percent))
                .filter(percent => !Number.isNaN(percent) && percent > 0);
            if (percents.length === 0) {
                return bold(t('commission_rule_summary_tiered'));
            }
            return <>{bold(t('commission_rule_summary_tiered'))} ({t('commission_rule_summary_tiered_range', {
                min: Math.min(...percents), max: Math.max(...percents)
            })})</>;
        }
        const percent = values.percent;
        return percent === '' || percent == null ? missing : bold(`${percent}%`);
    };

    const onWhat = () => {
        const scopeType = values.scopeType?.value;
        switch (scopeType) {
            case SCOPE_VALUE.ALL:
                return <>{t('commission_rule_summary_in')} {bold(t('commission_rule_summary_all_items'))}</>;
            case SCOPE_VALUE.ONLY_PRODUCTS:
                return <>{t('commission_rule_summary_in')} {bold(t('commission_rule_summary_all_products'))}</>;
            case SCOPE_VALUE.ONLY_SERVICES:
                return <>{t('commission_rule_summary_in')} {bold(t('commission_rule_summary_all_services'))}</>;
            case SCOPE_VALUE.CATEGORY:
                return <>{t('commission_rule_summary_in_category')} {values.scope ? bold(values.scope.label) : missing}</>;
            case SCOPE_VALUE.PRODUCT:
                return <>{t('commission_rule_summary_in')} {values.scope ? bold(values.scope.label) : missing}</>;
            default:
                return <>{t('commission_rule_summary_in')} {missing}</>;
        }
    };

    const where = values.branch
        ? <>{t('commission_rule_summary_in_branch')} {bold(values.branch.label)}</>
        : <>{t('commission_rule_summary_in')} {bold(t('commission_rule_summary_all_branches'))}</>;

    const from = formatDay(values.validFrom);
    const to = formatDay(values.validTo);

    return (
        <div className="commission-rule-summary" aria-live="polite">
            <span className="commission-rule-summary__title">
                <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                {t('commission_rule_summary_title')}
            </span>
            <p className="commission-rule-summary__text">
                {who} {howMuch()} {onWhat()}, {where}, {t('commission_rule_summary_from')} {from ? bold(from) : missing}
                {to
                    ? <>, {t('commission_rule_summary_until')} {bold(to)}.</>
                    : <>, {t('commission_rule_summary_no_end')}.</>}
            </p>
            {/* A prioridade entre regras só importa para quem escolhe um colaborador, e é aí
                que ela surpreende: a regra dele passa a valer no lugar da regra geral. */}
            {values.employee && (
                <small className="commission-rule-summary__note">
                    {t('commission_rule_summary_employee_note', { name: values.employee.label })}
                </small>
            )}
        </div>
    );
};

export default CommissionRuleSummary;
